import { t } from '@luna/i18n';

import type { AliasChangeError, AliasChangeValidationConfig } from './typing';

type AliasValidator = (alias: string, validationConfig: AliasChangeValidationConfig) => boolean;

const isValidAlias: AliasValidator = (alias, { allowedCharacters }): boolean =>
    allowedCharacters.test(alias);

const isAliasTooLong: AliasValidator = (alias, { maxLength }): boolean => alias.length > maxLength;

const isAliasTooShort: AliasValidator = (alias, { minLength = 0 }): boolean =>
    alias.length < minLength;

export const validateAlias = (
    alias: string,
    validationConfig: AliasChangeValidationConfig,
): AliasChangeError => {
    if (isAliasTooShort(alias, validationConfig)) {
        return [
            'account.details.validation_accountNameTooShort',
            'alias-change-error-alias-too-short',
        ];
    }

    if (isAliasTooLong(alias, validationConfig)) {
        const { maxLengthErrorMessage } = validationConfig;
        const errorMessage =
            maxLengthErrorMessage ?? 'account.details.validation_accountNameTooLong';

        return [errorMessage, 'alias-change-error-alias-too-long'];
    }

    if (alias && !isValidAlias(alias, validationConfig)) {
        return [
            'account.details.validation_accountNameInvalidCharacters',
            'alias-change-error-alias-invalid',
        ];
    }

    return [];
};

export const getApiErrorMessage = (errors: Array<any> = []): AliasChangeError => {
    if (errors.some((e) => e.error === 'VALUE_ALREADY_EXISTS')) {
        return ['account.profile.aliasAlreadyExists', 'alias-change-error-alias-already-exists'];
    }

    return ['products.error.title', 'alias-change-error-unknown-error'];
};

const ALIAS_CHANGE_ERROR_TRANSLATION_MAP: Record<string, () => string> = {
    'account.details.validation_accountNameTooShort': () =>
        t('account.details.validation_accountNameTooShort'),
    'account.details.validation_accountNameTooLong': () =>
        t('account.details.validation_accountNameTooLong'),
    'account.details.validation_accountNameInvalidCharacters': () =>
        t('account.details.validation_accountNameInvalidCharacters'),
    'account.profile.aliasAlreadyExists': () => t('account.profile.aliasAlreadyExists'),
    'products.error.title': () => t('products.error.title'),
};

export const getTranslatedAliasChangeError = (error: string): string => {
    return ALIAS_CHANGE_ERROR_TRANSLATION_MAP[error]?.() ?? error;
};
